/**
 * dSubState.ts — the D sub-state rule for a pack-page owner (graduated from narcis's inline
 * `ownerSubState` derivation and ovid's `ownerDGenState.ts`, 2026-07-29 —
 * thinking/plans/2026_07_28_counter_ledger_and_pack_factorisation.md). Both products reached
 * state D (`visitorState === 'owner'`) the same way once ovid gained its own pack-page action
 * (`a681bd47`): the click POSTs to the route action, which creates the grant synchronously via
 * `ensureRunGrant` and redirects; the redirect re-runs the loader, whose `resolveDeliveredPackPhotos`
 * picks the newest grant by `createdAt desc`. `ownerRun` — the loader-resolved run for THIS pack,
 * undefined/null when no grant exists at all — is therefore the sole input; there is no client
 * status to reconcile against.
 *
 * `label` is 'never_started' with no `ownerRun`, 'rendering' while `ownerRun.status ===
 * 'in_progress'` (regardless of how many slots are bound yet — a fresh grant reads 'rendering' at
 * 0 of N, not 'never_started'), 'delivered' once `ownerRun.status === 'complete'`. `slots` is
 * always exactly `nImages` long: a bound slot renders 'done', the first still-unbound slot renders
 * 'streaming' while rendering (the driver binds slots 0..N in order — packRun.server.ts — so the
 * frontier slot is genuinely the one in flight), everything else 'pending'.
 */

export interface OwnerRunSlot {
    slotIndex: number
    imageId: string | null
    imageUrl: string | null
}

export interface OwnerRun {
    status: 'in_progress' | 'complete'
    /** A specific `PackGrant` — the paid pack page's own ledger. Null when this run resolved from a
     *  wall batch instead (narcis wall plan step 5, step-5 review S3) — see `batchId`. Ovid never
     *  sets this null (its own `deliveredPhotos.server.ts` has no batch concept). */
    grantId: string | null
    createdAt: string
    boundCount: number
    maxGenerations: number
    slots: OwnerRunSlot[]
    /** Ovid muses (thinking/plans/2026_08_23_ovid_muses.md, Step 3) — the grant's own stamped
     *  `pack_grants.museSlug`, provenance for the D-gen/D-del tagline ("{Name} is on set now." /
     *  "Rendered as {Name}."). Optional: narcis never sets it (no muse concept), and it's absent
     *  for a chance-rendered ovid grant too. */
    museSlug?: string | null
    /** The wall batch this run resolved from, when `grantId` is null (step-5 review S3) — the
     *  delivery lives on the wall, not the paid ledger, so a caller that wants to link back to it
     *  reads this instead. Optional: ovid never sets it (no wall-batch delivery concept). */
    batchId?: string | null
}

export type DSlotState =
    | {status: 'pending'}
    | {status: 'streaming'}
    | {status: 'done'; imageId: string; imageUrl: string}

export type DSubStateLabel = 'never_started' | 'rendering' | 'delivered'

export interface DSubState {
    label: DSubStateLabel
    slots: DSlotState[]
}

export function deriveDSubState(ownerRun: OwnerRun | null | undefined, nImages: number): DSubState {
    const label: DSubStateLabel =
        !ownerRun ? 'never_started' :
        ownerRun.status === 'in_progress' ? 'rendering' : 'delivered'

    const firstUnboundSlot = ownerRun?.slots.findIndex((s) => s.imageId === null) ?? -1
    const slots: DSlotState[] = Array.from({length: nImages}, (_, i): DSlotState => {
        const slot = ownerRun?.slots[i]
        if (slot?.imageId && slot.imageUrl) return {status: 'done', imageId: slot.imageId, imageUrl: slot.imageUrl}
        if (label === 'rendering' && i === firstUnboundSlot) return {status: 'streaming'}
        return {status: 'pending'}
    })

    return {label, slots}
}
